import axios from 'axios';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://northlight-production.up.railway.app'
  : (process.env.REACT_APP_API_URL || 'http://localhost:5050');

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const API_URL = API_BASE_URL;

// ---- Auth token handling ----
const TOKEN_KEY = 'phalanx_token';

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
}
export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

// Attach the bearer token to every apiClient request.
apiClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// On an expired/invalid session (401) for a non-login request, drop the token
// and bounce to /login. Login endpoints return their own 401s — leave those.
apiClient.interceptors.response.use(
  (res) => res,
  (error) => {
    const url = error?.config?.url || '';
    const isAuthCall = url.includes('/api/auth/login') || url.includes('/api/auth/google');
    if (error?.response?.status === 401 && !isAuthCall) {
      clearToken();
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    }
    return Promise.reject(error);
  }
);

// ---- Auth API ----
function errMsg(e, fallback) {
  return e?.response?.data?.error || fallback;
}

export async function apiLogin(email, password) {
  const { data } = await apiClient.post('/api/auth/login', { email, password });
  return data; // { token, user }
}
export async function apiGoogleLogin(credential) {
  const { data } = await apiClient.post('/api/auth/google', { credential });
  return data; // { token, user }
}
export async function apiGetMe() {
  const { data } = await apiClient.get('/api/auth/me');
  return data.user;
}

// ---- Admin user-management API ----
export async function apiListUsers() {
  const { data } = await apiClient.get('/api/users');
  return data.users;
}
export async function apiCreateUser(payload) {
  const { data } = await apiClient.post('/api/users', payload);
  return data.user;
}
export async function apiUpdateUser(id, payload) {
  const { data } = await apiClient.patch(`/api/users/${id}`, payload);
  return data.user;
}
export async function apiResetUserPassword(id, password) {
  const { data } = await apiClient.post(`/api/users/${id}/password`, { password });
  return data;
}
export async function apiDeactivateUser(id) {
  const { data } = await apiClient.delete(`/api/users/${id}`);
  return data;
}

export { errMsg as apiErrorMessage };

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0']);

export function isLocalDev(hostname = window.location.hostname) {
  return LOCAL_HOSTS.has(hostname);
}

/** Use Vercel serverless /api routes (same-origin) instead of Railway. */
export function usesVercelFunctions(hostname = window.location.hostname) {
  if (isLocalDev(hostname)) return false;
  return (
    hostname.includes('vercel.app') ||
    hostname.endsWith('phalanxresearch.com') ||
    hostname.endsWith('researchphalanx.com') ||
    hostname.endsWith('pbandi.co.uk')
  );
}

export async function vercelGet(path, { cache = 'default' } = {}) {
  const url = path.startsWith('/') ? path : `/${path}`;
  const response = await fetch(url, { cache });
  if (!response.ok) {
    throw new Error(`Request failed: ${url} (${response.status})`);
  }
  return response;
}

export async function apiGetJson(path, { cache = 'default' } = {}) {
  if (usesVercelFunctions()) {
    const response = await vercelGet(path, { cache });
    return response.json();
  }
  const response = await apiClient.get(path);
  return response.data;
}

export async function fetchShortSellerReports({ page, limit, company, activist, sort = 'desc' }) {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    sort,
  });
  if (company) params.set('company', company);
  if (activist) params.set('activist', activist);

  const path = `/api/short-sellers?${params}`;

  if (usesVercelFunctions()) {
    const response = await vercelGet(path, { cache: 'no-store' });
    const data = await response.json();
    const total =
      parseInt(response.headers.get('X-Total-Count') || '', 10) ||
      parseInt(data?.[0]?.total_count || '', 10) ||
      (Array.isArray(data) ? data.length : 0);
    return { rows: Array.isArray(data) ? data : [], total };
  }

  const response = await apiClient.get('/api/short-sellers', {
    params: Object.fromEntries(params),
  });
  const rows = Array.isArray(response.data) ? response.data : [];
  const total =
    parseInt(response.headers['x-total-count'] || '', 10) ||
    parseInt(rows[0]?.total_count || '', 10) ||
    rows.length;
  return { rows, total };
}

export async function fetchActivistAttacks() {
  if (usesVercelFunctions()) {
    return apiGetJson('/api/analysis/all?type=activist-attacks', { cache: 'no-store' });
  }
  return apiGetJson('/api/analysis/activist-attacks');
}

export async function fetchActivistProfile(activistName) {
  const path = `/api/analysis/activist-profile/${encodeURIComponent(activistName)}`;
  return apiGetJson(path, { cache: 'no-store' });
}

export async function fetchAnalysisDashboard() {
  if (usesVercelFunctions()) {
    return apiGetJson('/api/analysis/all', { cache: 'no-store' });
  }
  const [companiesRes, sectorsRes, timelineRes] = await Promise.all([
    apiClient.get('/api/analysis/most-targeted-companies'),
    apiClient.get('/api/analysis/sector-attacks'),
    apiClient.get('/api/analysis/timeline'),
  ]);
  return {
    companies: companiesRes.data || [],
    sectorSummary: sectorsRes.data?.sectorSummary || [],
    frequency: timelineRes.data?.frequency || [],
  };
}

async function fetchOptionalJson(path) {
  try {
    if (usesVercelFunctions()) {
      const response = await vercelGet(path, { cache: 'no-store' });
      if (!response.ok) return null;
      return response.json();
    }
    const response = await apiClient.get(path.split('?')[0], {
      params: Object.fromEntries(new URLSearchParams(path.split('?')[1] || '')),
    });
    return response.data;
  } catch {
    return null;
  }
}

/** Homepage hero stats — cache-busted on production to avoid stale CDN counts. */
export async function fetchHomepageStats() {
  const ts = Date.now();
  // In production the client hits the consolidated /api/analysis/all
  // function directly (summary + yearly-stats were folded into it as
  // ?type= dispatches to stay under Vercel Hobby's 12-function cap).
  // The Express server on Railway still exposes the split routes, so
  // dev preserves the original URL shape.
  const useVercel = usesVercelFunctions();
  const summaryPath = useVercel
    ? `/api/analysis/all?type=summary&_=${ts}`
    : `/api/analysis/summary?_=${ts}`;
  const yearlyPath = useVercel
    ? `/api/analysis/all?type=yearly-stats&_=${ts}`
    : `/api/analysis/yearly-stats?_=${ts}`;
  const [summary, yearly, latestReports] = await Promise.all([
    fetchOptionalJson(summaryPath),
    fetchOptionalJson(yearlyPath),
    fetchOptionalJson(`/api/short-sellers/latest/10?_=${ts}`),
  ]);
  return { summary, yearly, latestReports };
}

/** Flat fact rows (region, country, exchange, sector + counts) for the
 *  homepage Coverage panels, which pivot by any two dimensions client-side. */
export async function fetchBreakdown() {
  const ts = Date.now();
  const path = usesVercelFunctions()
    ? `/api/analysis/all?type=breakdown&_=${ts}`
    : `/api/analysis/breakdown?_=${ts}`;
  const data = await fetchOptionalJson(path);
  return { facts: Array.isArray(data?.facts) ? data.facts : [] };
}

export const apiFetch = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
};
